/**
 * BaseModal - Modal base padronizado com CVA
 *
 * Encapsula Dialog do shadcn/ui com variantes e props padronizadas.
 *
 * @example Modal simples
 * <BaseModal open={open} onOpenChange={setOpen} title="Título">
 *   <p>Conteúdo do modal</p>
 * </BaseModal>
 *
 * @example Modal com formulário
 * <BaseModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Editar Usuário"
 *   description="Preencha os campos abaixo"
 *   variant="form"
 *   size="lg"
 *   footer={<Button>Salvar</Button>}
 * >
 *   <form>...</form>
 * </BaseModal>
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// =============================================================================
// VARIANTS
// =============================================================================

const modalContentVariants = cva('', {
  variants: {
    size: {
      sm: 'sm:max-w-md',
      default: 'sm:max-w-lg',
      lg: 'sm:max-w-2xl',
      xl: 'sm:max-w-4xl',
      full: 'sm:max-w-[90vw] h-[90vh]',
    },
    variant: {
      default: '',
      form: 'overflow-y-auto max-h-[85vh]',
      detail: 'overflow-y-auto max-h-[85vh]',
      confirmation: 'sm:max-w-md',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

// =============================================================================
// TYPES
// =============================================================================

export interface BaseModalProps extends VariantProps<typeof modalContentVariants> {
  /** Modal aberto ou fechado */
  open: boolean;
  /** Callback para mudança de estado */
  onOpenChange: (open: boolean) => void;
  /** Título do modal */
  title: string;
  /** Descrição opcional */
  description?: string;
  /** Conteúdo do modal */
  children: React.ReactNode;
  /** Footer com ações */
  footer?: React.ReactNode;
  /** Ícone do título (opcional) */
  icon?: React.ReactNode;
  /** Mostrar botão de fechar no header */
  showCloseButton?: boolean;
  /** Classe adicional para o conteúdo */
  className?: string;
  /** Desabilitar fechamento ao clicar fora (alias: preventOutsideClose) */
  disableOutsideClick?: boolean;
  /** Alias para disableOutsideClick */
  preventOutsideClose?: boolean;
  /** Estado de loading (desabilita interações) */
  loading?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  icon,
  size,
  variant,
  showCloseButton = false,
  className,
  disableOutsideClick = false,
  preventOutsideClose = false,
  loading = false,
}) => {
  const shouldPreventClose = disableOutsideClick || preventOutsideClose;

  return (
    <Dialog open={open} onOpenChange={shouldPreventClose ? undefined : onOpenChange}>
      <DialogContent
        className={cn(modalContentVariants({ size, variant }), className)}
        onPointerDownOutside={shouldPreventClose ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={loading ? (e) => e.preventDefault() : undefined}
        hideCloseButton={showCloseButton}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              {icon}
              {title}
            </DialogTitle>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className={cn(variant === 'form' && 'space-y-4', variant === 'detail' && 'space-y-4')}>
          {children}
        </div>

        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// SPECIALIZED MODALS
// =============================================================================

/**
 * ConfirmationModal - Modal de confirmação de ação
 */
export interface ConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  loading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'default',
  loading = false,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <BaseModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      variant="confirmation"
      loading={loading}
      footer={
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Processando...' : confirmText}
          </Button>
        </div>
      }
    >
      <div />
    </BaseModal>
  );
};

export default BaseModal;
